// Call log filter state (mockup docs/design/mockups/_build/pages/12-calls.html) and its mapping to the
// GET calllog parameters (contracts.md §7). Classifications are category ids from GET calllog/classifications.

export const CALLS_STORAGE_KEY = 'vl-calls';
export const VIEWS_STORAGE_KEY = 'vl-calls-views';
export const COLUMNS_STORAGE_KEY = 'vl-calls-columns';
export const PAGE_SIZE = 25;
const MAX_LINKED_IDS = 50;
const QUERY_LISTS = { categoryIds: 'cls', outcomes: 'out', agentIds: 'agentIds', queueIds: 'queueIds' };

export const EMPTY_CALL_FILTERS = { q: '', cls: [], match: 'any', sent: [], lang: '', tr: false, out: [], agentIds: [], agentName: '', queueIds: [], queueName: '', page: 0 };

export const SENTIMENT_OPTIONS = ['Positive', 'Neutral', 'Mixed', 'Negative'];
export const OUTCOME_OPTIONS = ['Answered', 'Transferred', 'Voicemail', 'Abandoned'];
export const LANGUAGE_OPTIONS = [
  ['', 'Any language'],
  ['English', 'English'],
  ['Spanish', 'Spanish'],
];
const LANGUAGE_CODES = { English: 'en', Spanish: 'es' };

// The API's SentimentLevel names, grouped the way the call log shows them.
const SENTIMENT_LEVELS = {
  Positive: ['Positive', 'VeryPositive'],
  Neutral: ['Neutral'],
  Mixed: ['Mixed'],
  Negative: ['Negative', 'VeryNegative'],
};

export const displaySentiment = (level) => {
  if (!level || level === 'Unknown') return null;
  if (level === 'VeryPositive') return 'Positive';
  if (level === 'VeryNegative') return 'Negative';
  return level;
};

export const languageName = (tag) => {
  if (!tag) return null;
  const code = String(tag).toLowerCase().split('-')[0];
  if (code === 'en') return 'English';
  if (code === 'es') return 'Spanish';
  return tag;
};

export const languageCode = (tag) => (tag ? String(tag).split('-')[0].toUpperCase() : '');

export function readStored(key, fallback, storage = 'sessionStorage') {
  try {
    const raw = window[storage].getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
}

export function writeStored(key, value, storage = 'sessionStorage') {
  try {
    window[storage].setItem(key, JSON.stringify(value));
  } catch (e) {
    // Storage blocked: the value lasts for this page view only.
  }
}

export function normalizeFilters(value) {
  const v = value && typeof value === 'object' ? value : {};
  const list = (x) => (Array.isArray(x) ? x.filter((i) => typeof i === 'string') : []);
  const text = (x) => (typeof x === 'string' ? x.trim().slice(0, 200) : '');
  const agentIds = list(v.agentIds).slice(0, MAX_LINKED_IDS);
  const queueIds = list(v.queueIds).slice(0, MAX_LINKED_IDS);
  return {
    q: typeof v.q === 'string' ? v.q.trim().slice(0, 200) : '',
    cls: list(v.cls),
    match: v.match === 'all' ? 'all' : 'any',
    sent: list(v.sent).filter((s) => SENTIMENT_OPTIONS.includes(s)),
    lang: LANGUAGE_CODES[v.lang] ? v.lang : '',
    tr: v.tr === true,
    out: list(v.out).filter((o) => OUTCOME_OPTIONS.includes(o)),
    agentIds,
    agentName: agentIds.length ? text(v.agentName) : '',
    queueIds,
    queueName: queueIds.length ? text(v.queueName) : '',
    page: Number.isInteger(v.page) && v.page > 0 ? v.page : 0,
  };
}

/** GET calllog parameters: the global filter params plus the call log's own filters and the page. */
export function callListParams(filters, globalParams) {
  const params = { ...globalParams, page: filters.page + 1, pageSize: PAGE_SIZE };
  if (filters.q) params.searchText = filters.q;
  if (filters.cls.length) {
    params.categoryIds = filters.cls;
    params.classificationMatch = filters.match;
  }
  if (filters.sent.length) params.callerSentiments = filters.sent.flatMap((s) => SENTIMENT_LEVELS[s] || []);
  if (filters.lang) params.languages = LANGUAGE_CODES[filters.lang];
  if (filters.tr) params.hasTranscript = 'true';
  if (filters.out.length) params.outcomes = filters.out;
  if (filters.agentIds.length) params.agentIds = filters.agentIds;
  if (filters.queueIds.length) params.queueIds = filters.queueIds;
  return params;
}

/**
 * Call log filters from a link (/calls?categoryIds=…&agentIds=…&agentName=…&queueIds=…&queueName=…&outcomes=…);
 * null when the query names none of them. Lists are comma-separated or repeated keys; names label the chips.
 */
export function filtersFromQuery(search) {
  const query = new URLSearchParams(search || '');
  const listed = Object.keys(QUERY_LISTS).filter((key) => query.has(key));
  if (!listed.length) return null;
  const values = (key) => query.getAll(key).flatMap((v) => v.split(',')).map((v) => v.trim()).filter(Boolean);
  const outcome = (value) => OUTCOME_OPTIONS.find((o) => o.toLowerCase() === value.toLowerCase());
  const raw = Object.fromEntries(Object.entries(QUERY_LISTS).map(([key, field]) => [field, [...new Set(values(key))]]));
  return normalizeFilters({
    ...EMPTY_CALL_FILTERS,
    ...raw,
    out: raw.out.map(outcome).filter(Boolean),
    match: query.get('classificationMatch') === 'all' ? 'all' : 'any',
    agentName: query.get('agentName') || '',
    queueName: query.get('queueName') || '',
  });
}

/** /calls link that opens the call log filtered to `filters` (the keys filtersFromQuery reads). */
export function callsLink({ categoryIds = [], outcomes = [], agentIds = [], agentName, queueIds = [], queueName } = {}) {
  const query = new URLSearchParams();
  if (categoryIds.length) query.set('categoryIds', categoryIds.join(','));
  if (outcomes.length) query.set('outcomes', outcomes.join(','));
  if (agentIds.length) query.set('agentIds', agentIds.join(','));
  if (agentIds.length && agentName) query.set('agentName', agentName);
  if (queueIds.length) query.set('queueIds', queueIds.join(','));
  if (queueIds.length && queueName) query.set('queueName', queueName);
  const text = query.toString();
  return text ? `/calls?${text}` : '/calls';
}

/** Chip label of a linked agent or queue filter: its name, else a name from the loaded calls, else a count. */
export function linkedLabel(ids, name, calls, kind) {
  if (!ids.length) return '';
  if (name) return name;
  const idKey = kind === 'agent' ? 'agentId' : 'queueId';
  const nameKey = kind === 'agent' ? 'agentName' : 'queueName';
  const found = [...new Set((calls || []).filter((c) => c[idKey] && ids.some((id) => id.toLowerCase() === String(c[idKey]).toLowerCase())).map((c) => c[nameKey]).filter(Boolean))];
  if (found.length && found.length === ids.length) return found.join(', ');
  return `${ids.length} ${kind === 'agent' ? (ids.length === 1 ? 'agent' : 'agents') : ids.length === 1 ? 'queue' : 'queues'}`;
}

/** Export parameters for export/datasets/Calls (same filters, no paging). */
export function callExportParams(filters) {
  const { page, pageSize, ...rest } = callListParams(filters, {});
  return Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, Array.isArray(v) ? v.join(',') : v]));
}

export function hasCallFilters(filters) {
  return Boolean(filters.q) || filters.cls.length > 0 || filters.sent.length > 0 || Boolean(filters.lang) || filters.tr || filters.out.length > 0 || filters.agentIds.length > 0 || filters.queueIds.length > 0;
}

// Built-in saved views from the mockup, by category name (resolved against the account's taxonomy).
export const BUILT_IN_VIEWS = [
  { name: 'Escalations and legal threats', clsNames: ['Escalation Requested', 'Legal Threat'], match: 'any' },
  { name: 'Spanish calls with transcripts', lang: 'Spanish', tr: true },
  { name: 'Negative billing calls', clsNames: ['Assessment and Billing'], sent: ['Negative'] },
  { name: 'Emergencies', clsNames: ['Emergency'] },
];

/** Filters for a built-in view; null when the account's taxonomy lacks one of its categories. */
export function resolveView(view, categories) {
  const ids = (view.clsNames || []).map((name) => categories.find((c) => c.name === name)?.id);
  if (ids.some((id) => !id)) return null;
  return normalizeFilters({ ...EMPTY_CALL_FILTERS, ...view, cls: ids });
}

/** "Violation Notice calls, North" style name for a saved view. */
export function viewName(filters, categories, scopeLabel) {
  const parts = [];
  const names = filters.cls.map((id) => categories.find((c) => c.id === id)?.name).filter(Boolean);
  if (names.length) parts.push(names.join(filters.match === 'all' ? ' and ' : ' or '));
  if (filters.sent.length) parts.push(filters.sent.join(' or ').toLowerCase());
  if (filters.lang) parts.push(filters.lang);
  if (filters.out.length) parts.push(filters.out.join(' or ').toLowerCase());
  const head = parts.length ? `${parts.join(', ')} calls` : 'All calls';
  const handled = [filters.queueIds.length ? `in ${linkedLabel(filters.queueIds, filters.queueName, [], 'queue')}` : '', filters.agentIds.length ? `handled by ${linkedLabel(filters.agentIds, filters.agentName, [], 'agent')}` : ''].filter(Boolean).join(' ');
  return `${head}${handled ? ` ${handled}` : ''}${filters.q ? ` matching "${filters.q}"` : ''}${filters.tr ? ' with transcripts' : ''}${scopeLabel ? `, ${scopeLabel}` : ''}`;
}

/** The optional call-list columns; the community column is labelled with the account's term. */
export function optionalColumnLabel(key, label, accountTerms) {
  return key === 'community' && accountTerms ? accountTerms.community : label;
}

export const OPTIONAL_COLUMNS = [
  ['community', 'Community'],
  ['route', 'Route'],
  ['handled', 'Handled by'],
  ['duration', 'Duration'],
  ['classifications', 'Classifications'],
  ['sentiment', 'Sentiment'],
  ['language', 'Language'],
  ['transcript', 'Transcript'],
];
